import {
  ActionIcon,
  Box,
  Button,
  Group,
  Input,
  NumberInput,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { schemaResolver, useForm } from '@mantine/form';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import { Panel } from 'components/Panel';
import { SaveButton } from 'components/SaveButton';
import { i18nKeys, useTranslation } from 'locales';
import { useEffect, useMemo } from 'react';
import { z } from 'zod/v4';

import { type UpdateInvoiceLogsVariables, useLoadInvoiceLogs, useUpdateInvoiceLogs } from '../api';

const InvoiceLogFormSchema = z.object({
  id: z.string().optional(),
  prefix: z.string().min(1),
  counter: z.number().int().min(0),
  deletable: z.boolean(),
});

const InvoiceLogsFormSchema = z.object({
  invoiceLogs: z.array(InvoiceLogFormSchema),
});

type InvoiceLogsFormValues = z.infer<typeof InvoiceLogsFormSchema>;

const NEW_LOG = { prefix: '', counter: 0, deletable: true } as const;

export const InvoiceLogsCard = () => {
  const { t } = useTranslation();
  const { invoiceLogs, isInvoiceLogsLoading } = useLoadInvoiceLogs();
  const { updateInvoiceLogs, isUpdateInvoiceLogsLoading } = useUpdateInvoiceLogs();

  const form = useForm<InvoiceLogsFormValues>({
    initialValues: { invoiceLogs: [] },
    validate: schemaResolver(InvoiceLogsFormSchema, { sync: true }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: sync server-loaded logs into the form only when the fetched data changes, not on every render (the form object identity is unstable)
  useEffect(() => {
    if (!invoiceLogs) return;

    const values = { invoiceLogs };
    form.setInitialValues(values);
    form.setValues(values);
  }, [invoiceLogs]);

  const handleSave = () => {
    const payload: UpdateInvoiceLogsVariables = {
      invoiceLogs: form.getValues().invoiceLogs.map(({ id, prefix, counter }) => ({
        ...(id === undefined ? {} : { id }),
        prefix,
        counter,
      })),
    };

    updateInvoiceLogs(payload, { onSuccess: () => form.resetDirty() });
  };

  const logs = form.getValues().invoiceLogs;

  const savedCounterById = useMemo(
    () => new Map((invoiceLogs ?? []).map(({ id, counter }) => [id, counter])),
    [invoiceLogs],
  );

  return (
    <Panel
      title={t(i18nKeys.FORM.INVOICING.INVOICING_BOOK)}
      withBorder
      fullHeight
      pinFooter
      isLoading={isInvoiceLogsLoading}
      topRight={
        <Button
          variant="light"
          size="xs"
          radius="md"
          leftSection={<IconPlus size={16} />}
          onClick={() => form.insertListItem('invoiceLogs', { ...NEW_LOG })}
        >
          {t(i18nKeys.FORM.INVOICING.ADD_LOG)}
        </Button>
      }
      footer={
        <SaveButton
          onClick={handleSave}
          disabled={!form.isDirty()}
          loading={isUpdateInvoiceLogsLoading}
          hideIcon
        />
      }
    >
      <Stack my="md">
        {logs.map((log, index) => {
          const nextReference = log.prefix ? `${log.prefix} - ${log.counter + 1}` : '—';

          return (
            <Group key={log.id ?? `new-${index}`} align="end" wrap="nowrap" gap="sm">
              <TextInput
                flex={1}
                label={t(i18nKeys.FORM.INVOICING.PREFIX)}
                disabled={!log.deletable}
                {...form.getInputProps(`invoiceLogs.${index}.prefix`)}
              />
              <NumberInput
                w={130}
                min={log.deletable ? 0 : (savedCounterById.get(log.id ?? '') ?? 0)}
                allowDecimal={false}
                clampBehavior="blur"
                label={t(i18nKeys.FORM.INVOICING.CURRENT_COUNTER)}
                {...form.getInputProps(`invoiceLogs.${index}.counter`)}
              />
              <Input.Wrapper flex={1} label={t(i18nKeys.FORM.INVOICING.NEXT_INVOICE)}>
                <Box
                  mih={36}
                  px="sm"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: 'var(--mantine-radius-default)',
                    backgroundColor:
                      'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))',
                  }}
                >
                  <Text size="sm" fw={500} c={log.prefix ? undefined : 'dimmed'}>
                    {nextReference}
                  </Text>
                </Box>
              </Input.Wrapper>
              <Tooltip
                label={t(i18nKeys.FORM.INVOICING.LOG_NOT_DELETABLE)}
                disabled={log.deletable}
              >
                <Box component="span" display="inline-flex">
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    size={36}
                    disabled={!log.deletable}
                    aria-label={t(i18nKeys.DELETE)}
                    onClick={() => form.removeListItem('invoiceLogs', index)}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                </Box>
              </Tooltip>
            </Group>
          );
        })}
      </Stack>
    </Panel>
  );
};
